import React, { useState, useEffect, useCallback } from 'react';
import {
  getMerchantInfo,
  createMerchantApplication,
  createMerchantSession,
  getMerchantUnderwritingReviews,
} from '../services/paymentsService';

// Newton-branded copy for each Rainforest underwriting review reason. Keys
// match the field names returned in ``data.underwriting_reviews`` from
// Rainforest's GET /merchants/{id}/applications/{id} endpoint. See:
// https://docs.rainforestpay.com/docs/additional-verification-for-merchant-onboarding
const UNDERWRITING_REVIEW_COPY = {
  business_tax_id: {
    title: 'Confirm your business tax ID',
    summary: "We couldn't match your business's legal name to the Tax ID (EIN) on file with the IRS. Update either field so they match exactly what's printed on your IRS SS-4 letter.",
    documents: [],
  },
  business_verification: {
    title: 'Verify your business is actively registered',
    summary: "We couldn't confirm your business is registered with the state. Upload a recent document that proves your business is in good standing.",    documents: [
      'DBA Registration',
      'Articles of Incorporation',
      'Certificate of Formation',
      'Partnership Agreement',
      'State Business License',
      'Certificate of Status (Certificate of Good Standing)',
      'Corporate Bylaws or Operating Agreement (for LLCs)',
      'Business Tax Return',
    ],
  },
  funding_deposit_method: {
    title: 'Verify your funding bank account',
    summary: "We couldn't confirm the bank account you provided is open and belongs to your business. Double‑check the routing and account numbers, or upload proof of ownership.",
    documents: [
      'Voided check showing bank name, business legal name, routing and account number',
      'Bank letter on bank letterhead with the same details',
    ],
  },
  billing_deposit_method: {
    title: 'Verify your billing bank account',
    summary: "We couldn't confirm the billing bank account you provided. Update the account details or upload proof of ownership.",
    documents: [
      'Voided check showing bank name, business legal name, routing and account number',
      'Bank letter on bank letterhead with the same details',
    ],
  },
  financial_review_1: {
    title: 'Provide recent financial statements',
    summary: 'We need a closer look at your business volume before approval. Upload your most recent 3 months of processing statements OR 3 months of bank statements.',
    documents: [
      '3 months of processing statements (processor name, volume, fees, refunds)',
      '3 months of bank statements (beginning/ending balance, transaction activity)',
    ],
  },
  financial_review_2: {
    title: 'Provide expanded financial review',
    summary: 'Your application requires a deeper financial review. Please upload all three document types below.',
    documents: [
      '3 months of processing statements',
      '3 months of bank statements',
      '2 years of financial statements plus current year-to-date (balance sheet and P&L)',
    ],
  },
  owner_1_name_ssn: {
    title: "Confirm owner 1's name and SSN",
    summary: "We couldn't match owner 1's legal name and Social Security Number to IRS records. Update the name or SSN exactly as they appear on the owner's Social Security card.",
    documents: [],
  },
  owner_2_name_ssn: {
    title: "Confirm owner 2's name and SSN",
    summary: "We couldn't match owner 2's legal name and Social Security Number to IRS records. Update the name or SSN exactly as they appear on the owner's Social Security card.",
    documents: [],
  },
  owner_3_name_ssn: {
    title: "Confirm owner 3's name and SSN",
    summary: "We couldn't match owner 3's legal name and Social Security Number to IRS records. Update the name or SSN exactly as they appear on the owner's Social Security card.",
    documents: [],
  },
  owner_4_name_ssn: {
    title: "Confirm owner 4's name and SSN",
    summary: "We couldn't match owner 4's legal name and Social Security Number to IRS records. Update the name or SSN exactly as they appear on the owner's Social Security card.",
    documents: [],
  },
  other: {
    title: 'Additional information requested',
    summary: "We need additional information that can't be collected through the self-service flow. Our team has reached out to coordinate next steps — keep an eye on your email.",
    documents: [],
  },
};

const humanizeReviewKey = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Normalize Rainforest's underwriting_reviews payload into an array of
// open review reasons. Rainforest returns an object keyed by reason where
// each value is ``{ updated_at, file_upload_ids }``. We surface every
// non-null entry and merge in our Newton-branded copy.
const buildOpenReviews = (underwritingReviews) => {
  if (!underwritingReviews || typeof underwritingReviews !== 'object') return [];
  return Object.entries(underwritingReviews)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      const copy = UNDERWRITING_REVIEW_COPY[key] || {
        title: humanizeReviewKey(key),
        summary: 'We need additional information to complete this review.',
        documents: [],
      };
      const fileUploadIds = Array.isArray(value?.file_upload_ids) ? value.file_upload_ids : [];
      return {
        key,
        ...copy,
        fileCount: fileUploadIds.length,
        updatedAt: value?.updated_at || null,
      };
    });
};

// Map Rainforest merchant_status to user-friendly labels
// Per Rainforest docs: PENDING | ONBOARDING | ACTIVE | SUSPENDED | DEACTIVATED | CANCELED
const getMerchantStatusLabel = (status) => {
  const statusMap = {
    'PENDING': 'Pending Setup',
    'ONBOARDING': 'Onboarding',
    'ACTIVE': 'Active',
    'SUSPENDED': 'Suspended',
    'DEACTIVATED': 'Deactivated',
    'CANCELED': 'Canceled',
  };
  return statusMap[status] || status;
};

// Map Rainforest merchant_application_status to user-friendly labels
// Per Rainforest docs: CREATED | IN_PROGRESS | PROCESSING | NEEDS_INFORMATION | IN_REVIEW | COMPLETED | DECLINED
const getApplicationStatusLabel = (status) => {
  const statusMap = {
    'CREATED': 'Not Started',
    'IN_PROGRESS': 'In Progress',
    'PROCESSING': 'Under Review',
    'NEEDS_INFORMATION': 'Action Required',
    'IN_REVIEW': 'In Review',
    'COMPLETED': 'Approved',
    'DECLINED': 'Declined',
  };
  return statusMap[status] || status;
};

// Get styling based on merchant status
const getMerchantStatusStyle = (status) => {
  switch (status) {
    case 'ACTIVE':
      return { backgroundColor: '#e7f4ec', borderColor: '#c6e3d0', color: '#1d6b40' }; // Green - success
    case 'ONBOARDING':
      return { backgroundColor: '#e8f0f7', borderColor: '#c5d8ea', color: '#1f4e79' }; // Blue - in progress
    case 'SUSPENDED':
      return { backgroundColor: '#fdf3e3', borderColor: '#f3d9ae', color: '#a8520c' }; // Yellow - warning
    case 'DEACTIVATED':
    case 'CANCELED':
      return { backgroundColor: '#fdecea', borderColor: '#f5c6c1', color: '#912018' }; // Red - error
    case 'PENDING':
    default:
      return { backgroundColor: '#f7f8f7', borderColor: '#e3e7e4', color: '#3c4540' }; // Gray - default
  }
};

const getApplicationStatusStyle = (status) => {
  switch (status) {
    case 'COMPLETED':
      return { backgroundColor: '#e7f4ec', borderColor: '#c6e3d0', color: '#1d6b40' }; // Green - success
    case 'DECLINED':
      return { backgroundColor: '#fdecea', borderColor: '#f5c6c1', color: '#912018' }; // Red - error
    case 'NEEDS_INFORMATION':
      return { backgroundColor: '#fdf3e3', borderColor: '#f3d9ae', color: '#a8520c' }; // Yellow - warning
    case 'IN_PROGRESS':
    case 'PROCESSING':
    case 'IN_REVIEW':
      return { backgroundColor: '#e8f0f7', borderColor: '#c5d8ea', color: '#1f4e79' }; // Blue - in progress
    default:
      return { backgroundColor: '#f7f8f7', borderColor: '#e3e7e4', color: '#3c4540' }; // Gray - default
  }
};

const MerchantTab = ({ practiceId }) => {
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [merchantNotFound, setMerchantNotFound] = useState(false);
  const [creatingMerchant, setCreatingMerchant] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [openReviews, setOpenReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');

  const loadMerchantData = useCallback(async () => {
    try {
      setError('');
      const merchantData = await getMerchantInfo(practiceId);
      
      // Check the merchant_status from the API response
      if (merchantData?.merchant_status === 'not_found') {
        setMerchantNotFound(true);
        setMerchantInfo(null);
        localStorage.setItem('merchantStatus', 'not_found');
      } else if (merchantData?.merchant_status === 'found') {
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(false);
        const status = merchantData?.merchant?.merchant_status;
        if (status) {
          localStorage.setItem('merchantStatus', status);
        }
      } else {
        // Fallback for older API responses that don't have merchant_status
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(!merchantData?.merchant);
        const status = merchantData?.merchant?.merchant_status;
        if (status) {
          localStorage.setItem('merchantStatus', status);
        }
      }
    } catch (err) {
      setError('Failed to load merchant data. Please check your connection.');
      setMerchantInfo(null);
      setMerchantNotFound(false);
    }
  }, [practiceId]);

  const startMerchantApplication = async () => {
    try {
      setCreatingMerchant(true);
      setError('');
      
      // Call the POST endpoint to create merchant application
      const merchantData = await createMerchantApplication(practiceId);
      setMerchantInfo(merchantData?.merchant || null);
      setMerchantNotFound(false);
      
    } catch (err) {
      setError('Failed to start merchant application. Please try again.');
    } finally {
      setCreatingMerchant(false);
    }
  };

  const startOnboardingSession = async () => {
    try {
      setCreatingSession(true);
      setError('');
      
      // Create a session for the Rainforest onboarding component
      const sessionResponse = await createMerchantSession(practiceId);
      setSessionData(sessionResponse);
      
    } catch (err) {
      setError('Failed to create onboarding session. Please try again.');
    } finally {
      setCreatingSession(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await loadMerchantData();
      } catch (err) {
        setError('Failed to load merchant data. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    if (practiceId) {
      loadData();
    }
  }, [practiceId, loadMerchantData]);

  // When the merchant application is awaiting more information, fetch the
  // live underwriting_reviews from Rainforest so we can render exactly
  // which reasons are open. We avoid persisting this to Cosmos for now —
  // every page open hits Rainforest, which keeps the data fresh and means
  // resolved reviews disappear immediately once the merchant resubmits.
  useEffect(() => {
    const status = merchantInfo?.merchant_application_status;
    if (!practiceId || status !== 'NEEDS_INFORMATION') {
      setOpenReviews([]);
      setReviewsError('');
      return;
    }

    let cancelled = false;
    const loadReviews = async () => {
      try {
        setReviewsLoading(true);
        setReviewsError('');
        const resp = await getMerchantUnderwritingReviews(practiceId);
        if (cancelled) return;
        setOpenReviews(buildOpenReviews(resp?.underwriting_reviews));
      } catch (err) {
        if (!cancelled) {
          setReviewsError("We couldn't load the specific items we need. Open the application below to see the requested details.");
          setOpenReviews([]);
        }
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    };
    loadReviews();
    return () => { cancelled = true; };
  }, [practiceId, merchantInfo?.merchant_application_status]);

  // Add event listeners for Rainforest component
  useEffect(() => {
    if (sessionData) {
      const comp = document.querySelector('rainforest-merchant-onboarding');
      
      const handleLoaded = () => {
        // Component loaded successfully
      };
      
      const handleSubmitted = (e) => {
        setSuccessMessage('🎉 Application submitted successfully! Your application is now being processed.');
        setError('');
        // Clear session data to hide the form
        setSessionData(null);
        // Reload merchant data to get updated status
        setTimeout(() => {
          loadMerchantData();
        }, 1000);
      };
      
      const handleError = (e) => {
        setError('Onboarding error: ' + (e.detail?.message || 'Unknown error'));
      };
      
      if (comp) {
        comp.addEventListener('loaded', handleLoaded);
        comp.addEventListener('submitted', handleSubmitted);
        comp.addEventListener('error', handleError);
        
        return () => {
          comp.removeEventListener('loaded', handleLoaded);
          comp.removeEventListener('submitted', handleSubmitted);
          comp.removeEventListener('error', handleError);
        };
      }
    }
  }, [sessionData, loadMerchantData]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading merchant data...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <p style={{ color: '#b42318', marginBottom: '20px' }}>⚠️ {error}</p>}
      {successMessage && <p style={{ color: '#1d6b40', marginBottom: '20px' }}>{successMessage}</p>}

      {/* Merchant Information Section */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        {merchantInfo ? (
          <div>
            <div style={{ 
              backgroundColor: '#e6efe9', 
              padding: '15px', 
              borderRadius: '4px',
              marginBottom: '15px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1f5a3d' }}>Next Steps</h4>
              
              {/* Status Tags - Per Rainforest docs: merchant_status (PENDING/ACTIVE) and merchant_application_status (CREATED/IN_PROGRESS/PROCESSING/NEEDS_INFORMATION/IN_REVIEW/COMPLETED/DECLINED) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px', alignItems: 'center', justifyContent: 'center' }}>
                {merchantInfo?.merchant_status && (() => {
                  const style = getMerchantStatusStyle(merchantInfo.merchant_status);
                  return (
                    <span style={{ 
                      padding: '4px 12px',
                      backgroundColor: style.backgroundColor,
                      border: `1px solid ${style.borderColor}`,
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      color: style.color,
                    }}>
                      Merchant: {getMerchantStatusLabel(merchantInfo.merchant_status)}
                    </span>
                  );
                })()}
                {merchantInfo?.merchant_application_status && (() => {
                  const style = getApplicationStatusStyle(merchantInfo.merchant_application_status);
                  return (
                    <span style={{ 
                      padding: '4px 12px',
                      backgroundColor: style.backgroundColor,
                      border: `1px solid ${style.borderColor}`,
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      color: style.color,
                    }}>
                      Application: {getApplicationStatusLabel(merchantInfo.merchant_application_status)}
                    </span>
                  );
                })()}
              </div>
              
              <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#1f5a3d' }}>
                {merchantInfo?.merchant_application_status === 'COMPLETED'
                  ? 'Congratulations! Your merchant application has been approved. You can now process payments.'
                  : merchantInfo?.merchant_application_status === 'PROCESSING'
                  ? 'Your merchant application is being processed. You can review your application status below.'
                  : merchantInfo?.merchant_application_status === 'NEEDS_INFORMATION'
                  ? 'Additional information is required for your application. Please provide the requested details below.'
                  : merchantInfo?.merchant_application_status === 'IN_REVIEW'
                  ? 'Your application is in review. We will contact you if more information is needed.'
                  : merchantInfo?.merchant_application_status === 'DECLINED'
                  ? 'Your merchant application was declined. Please contact support for more information.'
                  : 'Your merchant application has been created. Click the button below to start the onboarding process.'
                }
              </p>
              
              <button 
                onClick={startOnboardingSession}
                disabled={creatingSession || merchantInfo?.merchant_application_status === 'DECLINED'}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (creatingSession || merchantInfo?.merchant_application_status === 'DECLINED') ? '#6b716d' : '#1f5a3d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (creatingSession || merchantInfo?.merchant_application_status === 'DECLINED') ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                {creatingSession 
                  ? 'Creating Session...' 
                  : merchantInfo?.merchant_application_status === 'DECLINED'
                    ? 'Application Declined'
                    : ['PROCESSING', 'NEEDS_INFORMATION', 'IN_REVIEW'].includes(merchantInfo?.merchant_application_status)
                    ? 'Review Application'
                    : merchantInfo?.merchant_application_status === 'COMPLETED'
                    ? 'View Completed Application'
                    : '🔗 Start Onboarding'
                }
              </button>
            </div>

            {/* Newton-branded underwriting review breakdown. Shown only
                while the application is in NEEDS_INFORMATION so users
                see exactly what Rainforest is asking for before they
                open the embedded onboarding component.

                The explicit ``textAlign: 'left'`` overrides the
                ``text-align: center`` the parent settings page inherits
                — without this every line in the panel ends up centered
                including bullet list items. */}
            {merchantInfo?.merchant_application_status === 'NEEDS_INFORMATION' && (() => {
              const total = openReviews.length;
              const completed = openReviews.filter((r) => r.fileCount > 0).length;
              const remaining = Math.max(total - completed, 0);
              return (
              <div style={{
                textAlign: 'left',
                border: '1px solid #e3e7e4',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '20px 24px',
                marginBottom: '15px',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              }}>
                {/* Header row: icon + eyebrow + progress */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '16px',
                  marginBottom: '4px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      backgroundColor: '#fdf3e3',
                      color: '#a8520c',
                      fontSize: '1rem',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>!</span>
                    <div>
                      <p style={{
                        margin: 0,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#a8520c',
                      }}>Action required</p>
                      <h4 style={{
                        margin: '2px 0 0 0',
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        color: '#16241c',
                      }}>A few details before we can approve your account</h4>
                    </div>
                  </div>
                  {total > 0 && (
                    <span style={{
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderRadius: '999px',
                      backgroundColor: remaining === 0 ? '#e7f4ec' : '#f7f8f7',
                      border: `1px solid ${remaining === 0 ? '#c6e3d0' : '#e3e7e4'}`,
                      color: remaining === 0 ? '#1d6b40' : '#3c4540',
                      whiteSpace: 'nowrap',
                      alignSelf: 'flex-start',
                    }}>
                      {remaining === 0 ? 'All items submitted' : `${remaining} of ${total} pending`}
                    </span>
                  )}
                </div>

                <p style={{
                  margin: '10px 0 18px 48px',
                  fontSize: '0.9rem',
                  color: '#3c4540',
                  lineHeight: 1.5,
                }}>
                  Your application is on hold pending the items below. Provide them through the secure form and we'll typically have a decision back within 2 business days.
                </p>

                {reviewsLoading && (
                  <p style={{ margin: '0 0 0 48px', fontSize: '0.9rem', color: '#6b716d', fontStyle: 'italic' }}>
                    Loading the specific items requested…
                  </p>
                )}

                {!reviewsLoading && reviewsError && (
                  <p style={{ margin: '0 0 0 48px', fontSize: '0.9rem', color: '#a8520c' }}>{reviewsError}</p>
                )}

                {!reviewsLoading && !reviewsError && openReviews.length === 0 && (
                  <p style={{ margin: '0 0 0 48px', fontSize: '0.9rem', color: '#6b716d' }}>
                    No outstanding items were returned. Open the application below to view the latest from our team.
                  </p>
                )}

                {!reviewsLoading && openReviews.length > 0 && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {openReviews.map((review, idx) => {
                        const resolved = review.fileCount > 0;
                        return (
                        <div key={review.key} style={{
                          backgroundColor: resolved ? '#e7f4ec' : '#ffffff',
                          border: `1px solid ${resolved ? '#c6e3d0' : '#e3e7e4'}`,
                          borderRadius: '10px',
                          padding: '16px 18px',
                          display: 'flex',
                          gap: '14px',
                          alignItems: 'flex-start',
                        }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '28px',
                            height: '28px',
                            borderRadius: '999px',
                            backgroundColor: resolved ? '#1d6b40' : '#eef0ee',
                            color: resolved ? 'white' : '#3c4540',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            flexShrink: 0,
                            marginTop: '2px',
                          }}>{resolved ? '✓' : idx + 1}</span>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: '10px',
                              flexWrap: 'wrap',
                            }}>
                              <h5 style={{
                                margin: 0,
                                color: '#16241c',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                              }}>{review.title}</h5>
                              <span style={{
                                padding: '3px 10px',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                borderRadius: '999px',
                                backgroundColor: resolved ? '#e7f4ec' : '#fdf3e3',
                                border: `1px solid ${resolved ? '#c6e3d0' : '#f3d9ae'}`,
                                color: resolved ? '#1d6b40' : '#a8520c',
                                whiteSpace: 'nowrap',
                              }}>
                                {resolved
                                  ? `${review.fileCount} file${review.fileCount === 1 ? '' : 's'} uploaded`
                                  : 'Awaiting upload'}
                              </span>
                            </div>
                            <p style={{
                              margin: '6px 0 0 0',
                              fontSize: '0.88rem',
                              color: '#3c4540',
                              lineHeight: 1.5,
                            }}>
                              {review.summary}
                            </p>
                            {review.documents && review.documents.length > 0 && (
                              <div style={{ marginTop: '14px' }}>
                                <p style={{
                                  margin: '0 0 8px 0',
                                  fontSize: '0.72rem',
                                  color: '#6b716d',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.06em',
                                  fontWeight: 600,
                                }}>
                                  Any one of these works
                                </p>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                  gap: '6px 14px',
                                }}>
                                  {review.documents.map((doc, dIdx) => (
                                    <div key={dIdx} style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '8px',
                                      fontSize: '0.85rem',
                                      color: '#3c4540',
                                      lineHeight: 1.4,
                                    }}>
                                      <span style={{
                                        color: '#1d6b40',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        marginTop: '1px',
                                      }}>•</span>
                                      <span>{doc}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>

                    {remaining > 0 && !sessionData && (
                      <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-start' }}>
                        <button
                          onClick={startOnboardingSession}
                          disabled={creatingSession}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: creatingSession ? '#8a908c' : '#1d6b40',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: creatingSession ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            transition: 'background-color 150ms ease-out',
                          }}
                          onMouseOver={(e) => {
                            if (!creatingSession) e.currentTarget.style.backgroundColor = '#1d6b40';
                          }}
                          onMouseOut={(e) => {
                            if (!creatingSession) e.currentTarget.style.backgroundColor = '#1d6b40';
                          }}
                        >
                          {creatingSession ? 'Opening secure form…' : 'Provide information'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              );
            })()}

            {/* Rainforest Onboarding Component */}
            {sessionData && (
              <div style={{ 
                backgroundColor: '#f7f8f7', 
                padding: '20px', 
                borderRadius: '4px',
                marginBottom: '15px'
              }}>
                <h5 style={{ margin: '0 0 15px 0', color: '#1d6b40' }}>
                  Your Merchant Application
                </h5>
                
                {/* Rainforest Onboarding Component */}
                <div style={{ 
                  border: '1px solid #e3e7e4', 
                  padding: '20px', 
                  borderRadius: '8px',
                  backgroundColor: '#ffffff'
                }}>
                  <rainforest-merchant-onboarding
                    session-key={sessionData.session_key}
                    merchant-id={sessionData.merchant_id}
                    merchant-application-id={sessionData.merchant_application_id || ''}
                    terms-and-conditions-url="https://joinnewton.com/terms"
                  ></rainforest-merchant-onboarding>
                </div>
              </div>
            )}
          </div>
        ) : merchantNotFound ? (
          <div>
            <div style={{ 
              backgroundColor: '#fdf3e3', 
              padding: '15px', 
              borderRadius: '4px',
              marginBottom: '15px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#a8520c' }}>
                ⚠️ No Merchant Setup Found
              </h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#a8520c' }}>
                This practice doesn't have payment processing setup yet. Start the application process 
                to enable payment processing.
              </p>
              
              <button 
                onClick={startMerchantApplication}
                disabled={creatingMerchant}
                style={{
                  padding: '12px 24px',
                  backgroundColor: creatingMerchant ? '#6b716d' : '#1d6b40',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: creatingMerchant ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                {creatingMerchant ? 'Creating Application...' : '🚀 Start Merchant Application'}
              </button>
            </div>
          </div>
        ) : (
          <p style={{ color: '#6b716d', fontStyle: 'italic' }}>
            Loading merchant information...
          </p>
        )}
      </div>
    </div>
  );
};

export default MerchantTab;
